import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import * as fromRoot from '../store';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Modal, Notification } from '../store/notification/notification.interface';
import { getFirst$$ } from '../util/util';
import { goAction } from '../store/router/router.actions';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalMessageComponent } from 'app/shared/modal/modal-message.component';
import { ModalComponent } from 'app/shared/modal/modal.component';
import { AzureMonitoringService } from './AzureMonitoringService';

export interface SubmitFeedbackDialogParameters {
  isSuccess: boolean;
  messageType: string;
  data: {
    etmOrderNumber: string;
    salesOrderNumber: string;
    ccrNumber?: string;
    incoterm?: string;
    otherEtmOrderNumbers?: any;
  };
  buttons?: {
    showDownloadDocumentButton?: boolean;
    showCreateButton?: boolean;
    showCopyButton?: boolean;
  };
  methods?: {
    downloadDocument?: (documentId: string, extension: string) => void;
    copyFunction?: (etmOrderNumber: string) => void;
    createAction?: Action;
  };
  route: {
    moduleRoute: string;
    moduleRouteOverview?: string;
    backUrl: string;
  };
}

const defaultNotification: Notification = {
  disableClose: false,
  type: 'info',
};

const defaultModal: Modal = {
  buttonText: '',
  disableClose: false,
  type: 'info'
};

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private openDialogs: number = 0;

  constructor(private store: Store<fromRoot.RootState>, private toastrService: ToastrService, private translateService: TranslateService, private dialog: MatDialog, private azureMonitoringService: AzureMonitoringService) {}

  showToastr(partialNotification: Partial<Notification>): void {
    const notification = {
      ...defaultNotification,
      ...partialNotification,
    };

    const { message, title } = this.getMessages(notification);

    this.azureMonitoringService.logEvent(`TOASTR (${notification.type}): ${title}`, {message, title, type: notification.type});

    const options = {
      positionClass: 'toast-bottom-right',
      timeOut: 5000,
      tapToDismiss: true,
      closeButton: false,
    };

    switch (notification.type) {
      case 'error':
        Object.assign(options, { timeOut: 50000, extendedTimeOut: 50000, closeButton: true });
        this.toastrService.error(message, title, options);
        break;
      case 'warning':
        this.toastrService.warning(message, title, options);
        break;
      case 'info':
        this.toastrService.info(message, title, options);
        break;
      case 'success':
        Object.assign(options, { timeOut: 2000 });
        this.toastrService.success(message, title, options);
        break;
    }
  }

  showModal(notification: Partial<Notification> | Partial<Modal>): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const { message, title, subMessages } = this.getMessages(notification);
      this.azureMonitoringService.logEvent(`MODAL (${notification.type}): ${title}`, {message, title, type: notification.type, subMessages});
      let modal: Modal;
      switch (notification.type) {
        case 'error':
          modal = {
            ...defaultModal,
            title,
            message,
            subMessages,
            type: 'error',
            titleCode: 'ERROR',
            buttonText: 'SHARED.BUTTONS.CLOSE',
            ...notification,
            messageCode: null,
            disableClose: notification.disableClose || notification.showLogout,
          };
          break;
        case 'info':
          modal = {
            ...defaultModal,
            message,
            subMessages,
            title,
            type: 'info',
            titleCode: 'INFO',
            buttonText: 'SHARED.BUTTONS.CLOSE',
            ...notification,
            messageCode: null,
          };
          break;
        case 'warning':
          modal = {
            ...defaultModal,
            message,
            subMessages,
            title,
            type: 'warning',
            titleCode: 'WARNING',
            buttonText: 'SHARED.BUTTONS.CLOSE',
            ...notification,
            messageCode: null,
            disableClose: notification.disableClose || notification.showLogout,
          };
          break;
        case 'success':
          modal = {
            ...defaultModal,
            type: 'success',
            title,
            titleCode: 'SUCCESS',
            message,
            buttonText: 'SHARED.BUTTONS.CLOSE',
            ...notification,
            messageCode: null,
          };
          break;
      }

      if (this.openDialogs === 0) {
        this.openDialogs += 1;
        const config: MatDialogConfig = {
          width: '50%',
          height: 'auto',
          data: modal,
          role: 'dialog',
          disableClose: modal.disableClose,
        };
        const dialogRef = this.dialog.open(ModalMessageComponent, config);
        dialogRef.afterClosed().subscribe(() => {
          this.openDialogs -= 1;
          if (modal.redirectUrl) {
            // redirect to given redirectUrl
            this.store.dispatch(goAction({ path: modal.redirectUrl }));
          }
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  private getMessages(notification: Partial<Notification>) {
    const message = notification.message || (notification.messageCode && this.translateService.instant(notification.messageCode, notification.translationParams));
    const title = notification.title || (notification.titleCode && this.translateService.instant(notification.titleCode, notification.translationParams));
    const subMessages = notification.subMessages;
    return { message, title, subMessages };
  }

  async showSubmitFeedbackDialog(params: SubmitFeedbackDialogParameters) {
    const firstName = await getFirst$$(this.store.select(state => state.session.userContext.firstName));
    const messageType = params.messageType && params.messageType.trim() !== '' ? params.messageType.trim() + '.' : '';

    let bodyTranslationCode = `SHARED.MODAL.MSG_ERROR`;
    if (params.isSuccess) {
      if (params.data.ccrNumber) {
        bodyTranslationCode = `SHARED.MODAL.MSG_SUCCESS_CCR`;
      } else if (params.data.otherEtmOrderNumbers && params.data.otherEtmOrderNumbers.length > 0) {
        bodyTranslationCode = 'SHARED.MODAL.MSG_SUCCESS_SINGLE_RECURRENCE';
      } else if (!params.data.incoterm || params.data.incoterm === '-') {
        bodyTranslationCode = `SHARED.MODAL.MSG_SUCCESS`;
      } else {
        bodyTranslationCode = `SHARED.MODAL.MSG_DL40_SUCCESS`;
      }
    }

    const headerTranslationCode = params.isSuccess ? `SHARED.MODAL.${messageType}HEADER_SUCCESS` : `SHARED.MODAL.${messageType}HEADER_ERROR`;
    const showDownloadDocumentButton = params.buttons && params.buttons.showDownloadDocumentButton && params.methods && params.methods.downloadDocument;

    if (params.isSuccess && this.dialog.openDialogs.length === 0) {
      // If fail, we already have a dialog !
      let otherEtmOrders = null;
      if (params.data.otherEtmOrderNumbers && params.data.otherEtmOrderNumbers.length) {
        otherEtmOrders = params.data.otherEtmOrderNumbers.map(etm => `#${etm.etmOrderNumber}`).join(', ');
      }
      const modalData: object = {
        showDownloadDocumentButton,
        showCreateButton: params.buttons && params.methods && params.buttons.showCreateButton && params.methods.createAction,
        showCopyButton: params.buttons && params.methods && params.buttons.showCopyButton && params.methods.copyFunction,
        type: params.isSuccess ? 'success' : 'error',
        name: firstName,
        headerTranslationCode,
        bodyTranslationCode,
        otherEtmOrders,
        number: params.data.etmOrderNumber,
        ccrNumber: params.data.ccrNumber,
        detailButtonText: 'SHARED.BUTTONS.GO_TO_DETAIL',
        overviewButtonText: 'SHARED.BUTTONS.GO_TO_OVERVIEW',
        downloadButtonText: 'SHARED.BUTTONS.DOWNLOAD_DOCUMENT',
        createButtonText: 'SHARED.BUTTONS.CREATE_A_NEW_ONE',
        copyButtonText: 'SHARED.BUTTONS.COPY',
      };
      const config: MatDialogConfig = {
        width: '80%',
        height: 'auto',
        data: modalData,
        role: 'dialog',
        disableClose: true,
      };

      try {
        this.azureMonitoringService.logEvent(`SUBMIT FEEDBACK: ${headerTranslationCode}`, {config: JSON.stringify(config)});
      } catch (e) {
      }

      const dialogRef = this.dialog.open(ModalComponent, config);
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          // route.params remains empty so have to try to get them again
          if (result.downloadDocument && params.methods && params.methods.downloadDocument) {
            params.methods.downloadDocument(params.data.ccrNumber ? params.data.ccrNumber : params.data.etmOrderNumber, 'PDF');
          }

          // redirect to overview or detail page
          if (result.goToOverview) {
            // go to overview
            if (params.route.moduleRouteOverview) {
              this.store.dispatch(goAction({ path: `/${params.route.moduleRouteOverview}` }));
            } else {
              this.store.dispatch(goAction({ path: `/${params.route.moduleRoute}/refresh` }));
            }
          }
          if (result.goToDetail) {
            // go to detail
            if (params.data.salesOrderNumber) {
              this.store.dispatch(
                goAction({
                  path: [`/${params.route.moduleRoute}/${params.data.etmOrderNumber}`],
                  query: {
                    salesOrderNumber: params.data.salesOrderNumber,
                    back: params.route.backUrl,
                  },
                })
              );
            } else {
              this.store.dispatch(
                goAction({
                  path: [`/${params.route.moduleRoute}/${params.data.etmOrderNumber}`],
                  query: { back: params.route.backUrl },
                })
              );
            }
          }
          if (result.goToCreate && params.methods && params.methods.createAction) {
            // go to create
            this.store.dispatch(params.methods.createAction);
          }

          if (result.goToCopy && params.methods && params.methods.copyFunction) {
            params.methods.copyFunction(params.data.etmOrderNumber);
          }
        }
      });
    }
  }
}
